import {IssueReferenceIcon} from '@github-ui/issue-pr-state/IssueStatusIcon'
import {Link as RouterLink} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {ActionList} from '@primer/react'
import type React from 'react'

import {
  ISSUE_HEADER_LIVE_UPDATE_THROTTLE_MS,
  issueHeaderRefetchQueryKey,
  useIssueHeaderDataContext,
} from '../../header/IssueHeaderDataContext'
import type {ParentIssueNode, ParentIssueRepositoryNode} from './parent-issue-query-types'
import {SubIssuesSummary} from './SubIssuesSummary'

// The parent node carries the union of every fragment's parent selection, so we Pick out just the fields
// this component reads — including `subIssuesSummary`, which is forwarded to the (plain-props) `SubIssuesSummary` child.
// RELAY-CLEANUP(RelationshipsSection): `RelayCompat` widens nullable fields so the still-Relay
// RelationshipsSection parent data is assignable. Remove the wrapper when RelationshipsSection leaves Relay.
export type ParentIssueFragment = RelayCompat<
  Pick<
    ParentIssueNode,
    'id' | 'number' | 'state' | 'stateReason' | 'subIssuesSummary' | 'titleHTML' | 'updatesChannel' | 'url'
  > & {
    readonly status?: ParentIssueNode['status']
    readonly repository: Pick<ParentIssueRepositoryNode, 'nameWithOwner'>
  }
>

type ParentIssueAliveNotification = {
  wait?: number
  issue_state_updated?: true
  issue_title_updated?: true
  sub_issues_summary_updated?: true
}

function isParentIssueUpdated(data: ParentIssueAliveNotification): boolean {
  return Boolean(data.issue_state_updated || data.issue_title_updated || data.sub_issues_summary_updated)
}

type ParentIssueProps = {
  issue: ParentIssueFragment
  onLinkClick?: (event: MouseEvent) => void
}

export function ParentIssue({issue: data, onLinkClick}: ParentIssueProps) {
  const childIssueId = useIssueHeaderDataContext().id
  useInvalidateOnAliveUpdate<ParentIssueAliveNotification>(
    data.updatesChannel,
    issueHeaderRefetchQueryKey(childIssueId),
    {filter: isParentIssueUpdated, throttleTimeout: ISSUE_HEADER_LIVE_UPDATE_THROTTLE_MS},
  )
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const title = data.titleHTML as SafeHTMLString

  return (
    <ActionList.LinkItem
      as={RouterLink}
      to={data.url}
      target="_blank"
      onClick={
        onLinkClick
          ? (event: React.MouseEvent<HTMLElement>) => {
              onLinkClick(event.nativeEvent)
            }
          : undefined
      }
    >
      <ActionList.LeadingVisual>
        <IssueReferenceIcon issue={data} />
      </ActionList.LeadingVisual>
      <ActionList.TrailingVisual>
        <SubIssuesSummary issue={data} />
      </ActionList.TrailingVisual>
      <VerifiedHTMLText html={title} className="markdown-title" />
      <ActionList.Description variant="block">
        {data.repository.nameWithOwner}#{data.number}
      </ActionList.Description>
    </ActionList.LinkItem>
  )
}
